import { useCallback, useEffect } from "react";
import { FormControl } from "react-bootstrap";
import { Control, Controller } from "react-hook-form";
import { useTwilioLandlineCheck } from "src/hooks/useTwilioLandlineCheck";
import { isValidPhoneNumber, PHONE_INPUT_TRANSFORMER } from "src/utils";

interface IProps {
    control: Control<any>;
    name: string;
    defaultValue?: string;
    placeholder?: string;
    disabled?: boolean;
    onBlur?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    isInvalid?: boolean;
    autoComplete?: string;
    className?: string;
    id?: string;
    required?: boolean;
    skipVerify?: boolean;
}

const PhoneFormController: React.FC<IProps> = ({
    control,
    name,
    defaultValue,
    placeholder,
    disabled,
    onBlur,
    isInvalid,
    autoComplete,
    className,
    id,
    required,
    skipVerify,
}) => {
    const { verify, isNotLandline } = useTwilioLandlineCheck();
    useEffect(() => {
        if (!skipVerify) {
            verify(defaultValue);
        }
    }, [verify, defaultValue, skipVerify]);

    const handleBlur = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = e.target;

            if (!skipVerify && isValidPhoneNumber(value)) {
                verify(value);
            }
        },
        [verify, skipVerify],
    );

    return (
        <>
            <Controller
                defaultValue={defaultValue}
                control={control}
                name={name}
                render={({ field }) => (
                    <FormControl
                        type="tel"
                        id={id || name}
                        name={id || name}
                        autoComplete={autoComplete || "tel-national"}
                        isInvalid={isInvalid}
                        placeholder={placeholder === undefined ? "Phone Number" : placeholder}
                        disabled={disabled}
                        required={required}
                        onChange={(e) =>
                            field.onChange(
                                (() => {
                                    const output = PHONE_INPUT_TRANSFORMER.output(e);
                                    if (!skipVerify && isValidPhoneNumber(output)) {
                                        verify(output);
                                    }
                                    return output;
                                })(),
                            )
                        }
                        value={PHONE_INPUT_TRANSFORMER.input(field.value)}
                        onBlur={onBlur || handleBlur}
                        className={className}
                        maxLength={16}
                    />
                )}
            />
            {isNotLandline === false && (
                <div className="text-danger body-small my-1">
                    This phone number may not be capable of receiving SMS/text messages.
                </div>
            )}
        </>
    );
};

export default PhoneFormController;
